import { collection, addDoc, getDocs, query, orderBy, limit, Timestamp } from 'firebase/firestore'
import { db } from '../config/firebase'

export interface HighScoreEntry {
  name: string
  score: number
  date: string
  timestamp: number
  region?: string
}

export class LeaderboardService {
  private collectionName = 'highscores'
  private cachedScores: HighScoreEntry[] | null = null
  private cacheTimestamp: number = 0
  private cacheDuration = 5000 // Cache for 5 seconds

  /**
   * Submit a new high score to Firebase
   */
  async submitScore(name: string, score: number, region?: string): Promise<void> {
    // Clear cache when submitting new score
    this.cachedScores = null
    console.log('=== SUBMITTING SCORE ===')
    console.log('Name:', name)
    console.log('Score:', score)
    console.log('Region:', region || 'global')

    try {
      const scoreData = {
        name,
        score,
        date: new Date().toLocaleDateString(),
        timestamp: Timestamp.now().toMillis(),
        region: region || 'global',
      }

      console.log('Score data to submit:', scoreData)
      console.log('Collection name:', this.collectionName)
      console.log('Database:', db)

      const docRef = await addDoc(collection(db, this.collectionName), scoreData)
      console.log('✅ Score submitted successfully! Document ID:', docRef.id)
    } catch (error) {
      console.error('❌ Error submitting score:', error)
      console.error('Error details:', JSON.stringify(error, null, 2))
      throw error
    }
  }

  /**
   * Get top high scores from Firebase
   * @param maxResults Maximum number of scores to retrieve (default: 10)
   * @param region Optional region filter (e.g., 'UK', 'US', or 'global')
   */
  async getHighScores(maxResults: number = 10, region?: string): Promise<HighScoreEntry[]> {
    try {
      // Check cache first
      const now = Date.now()
      if (this.cachedScores && now - this.cacheTimestamp < this.cacheDuration) {
        console.log('📦 Using cached scores')
        return this.cachedScores.slice(0, maxResults)
      }

      console.log('🌐 Fetching scores from Firebase...')
      // Create query to get top scores ordered by score descending
      const scoresQuery = query(
        collection(db, this.collectionName),
        orderBy('score', 'desc'),
        limit(maxResults) // Only fetch what we need
      )

      const querySnapshot = await getDocs(scoresQuery)
      let scores: HighScoreEntry[] = []

      querySnapshot.forEach((doc) => {
        const data = doc.data()
        scores.push({
          name: data.name,
          score: data.score,
          date: data.date,
          timestamp: data.timestamp,
          region: data.region,
        })
      })

      // Filter by region if specified
      if (region && region !== 'global') {
        scores = scores.filter((s) => s.region === region)
      }

      // Cache the results
      this.cachedScores = scores
      this.cacheTimestamp = now
      console.log('✅ Scores fetched and cached')

      // Return only the requested number of results
      return scores.slice(0, maxResults)
    } catch (error) {
      console.error('Error fetching high scores:', error)
      // Return empty array on error so game doesn't crash
      return []
    }
  }

  /**
   * Check if a score qualifies for the leaderboard
   */
  async isHighScore(score: number, maxResults: number = 10): Promise<boolean> {
    try {
      console.log('🔍 Checking if score qualifies:', score)
      const currentHighScores = await this.getHighScores(maxResults)

      // If we have less than maxResults, any score qualifies
      if (currentHighScores.length < maxResults) {
        console.log('✅ Qualifies: Leaderboard not full')
        return true
      }

      // Check if score beats the lowest high score
      const lowestHighScore = currentHighScores[currentHighScores.length - 1]
      const qualifies = score > lowestHighScore.score
      console.log(`${qualifies ? '✅' : '❌'} Score ${qualifies ? 'beats' : 'does not beat'} lowest score:`, lowestHighScore.score)
      return qualifies
    } catch (error) {
      console.error('Error checking high score:', error)
      return true // Allow submission on error
    }
  }
}

// Export a singleton instance
export const leaderboardService = new LeaderboardService()
